import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { Icon } from '@/components/icon';

import styles from '../text-cell.module.scss';

export type BlockType = 'paragraph' | 'heading1' | 'heading2' | 'heading3';

interface BlockTypeState {
  isHeading1: boolean;
  isHeading2: boolean;
  isHeading3: boolean;
}

export interface BlockTypeDropdownProps {
  state: BlockTypeState;
  onChange: (blockType: BlockType) => void;
}

export const BlockTypeDropdown = (props: BlockTypeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentBlockType = getCurrentBlockType(props.state);

  const handleBlockTypeChange = (blockType: BlockType) => {
    props.onChange(blockType);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={styles.blockTypeDropdown} ref={dropdownRef}>
      <button className={styles.blockTypeButton} onClick={() => setIsOpen(!isOpen)}>
        {getBlockTypeIcon(currentBlockType)}
        <Icon name="ChevronDownThin" size={14} />
      </button>
      {isOpen && (
        <div className={styles.blockTypeMenu}>
          <button
            className={classNames(styles.blockTypeMenuItem, {
              [styles.active]: currentBlockType === 'paragraph',
            })}
            onClick={() => handleBlockTypeChange('paragraph')}>
            {getBlockTypeLabel('paragraph')}
          </button>
          <button
            className={classNames(styles.blockTypeMenuItem, styles.heading1, {
              [styles.active]: currentBlockType === 'heading1',
            })}
            onClick={() => handleBlockTypeChange('heading1')}>
            {getBlockTypeLabel('heading1')}
          </button>
          <button
            className={classNames(styles.blockTypeMenuItem, styles.heading2, {
              [styles.active]: currentBlockType === 'heading2',
            })}
            onClick={() => handleBlockTypeChange('heading2')}>
            {getBlockTypeLabel('heading2')}
          </button>
          <button
            className={classNames(styles.blockTypeMenuItem, styles.heading3, {
              [styles.active]: currentBlockType === 'heading3',
            })}
            onClick={() => handleBlockTypeChange('heading3')}>
            {getBlockTypeLabel('heading3')}
          </button>
        </div>
      )}
    </div>
  );
};

const getCurrentBlockType = (state: BlockTypeState): BlockType => {
  switch (true) {
    case state.isHeading1:
      return 'heading1';
    case state.isHeading2:
      return 'heading2';
    case state.isHeading3:
      return 'heading3';
    default:
      return 'paragraph';
  }
};

const getBlockTypeIcon = (blockType: BlockType) => {
  switch (blockType) {
    case 'heading1':
      return <Icon name="Heading1" size={16} />;
    case 'heading2':
      return <Icon name="Heading2" size={16} />;
    case 'heading3':
      return <Icon name="Heading3" size={16} />;
    default:
      return <Icon name="RegularText" size={16} />;
  }
};

const getBlockTypeLabel = (blockType: BlockType): string => {
  switch (blockType) {
    case 'heading1':
      return 'Heading 1';
    case 'heading2':
      return 'Heading 2';
    case 'heading3':
      return 'Heading 3';
    default:
      return 'Paragraph';
  }
};
